import { useState, useEffect } from "react";
import { Bell, Camera, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getPushState, enableWebPush } from "../lib/webPush";

// Home "Finish setting up" nudge (2026-06). Surfaces for ANYONE — new hires
// AND existing crew — whose notifications are off or who have no profile
// photo. (User: Michael was never told to turn on notifications; crew keep
// skipping photos.) A 24h "later" snooze keeps it reminding until done
// instead of nagging on every single open.
const SNOOZE_KEY = (id) => `wowSetupSnoozeUntil:${id}`;
const SNOOZE_MS = 24 * 60 * 60 * 1000;

export default function SetupNudgeCard({ crewId, hasPhoto, onAddPhoto }) {
  const [pushNeeded, setPushNeeded] = useState(false);
  const [pushDone, setPushDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snoozed, setSnoozed] = useState(false);

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(SNOOZE_KEY(crewId)) || 0);
      if (Date.now() < until) { setSnoozed(true); return; }
    } catch (_) { /* ignore */ }
    let active = true;
    (async () => {
      const st = await getPushState();
      if (active) setPushNeeded(st.supported && !(st.permission === "granted" && st.subscribed));
    })();
    return () => { active = false; };
  }, [crewId]);

  const needPhoto = !hasPhoto;
  const needPush = pushNeeded && !pushDone;

  if (snoozed || (!needPush && !needPhoto)) return null;

  const enable = async () => {
    setBusy(true);
    const res = await enableWebPush(crewId);
    setBusy(false);
    if (res.ok) { setPushDone(true); toast.success("Notifications on — you're all set!"); return; }
    if (res.reason === "not_pwa_ios") {
      toast("Add WoW to your Home Screen first", { description: "Tap the Share icon → Add to Home Screen, then turn on notifications." });
      return;
    }
    if (res.reason === "denied") {
      toast.error("Notifications are blocked — turn them on for WoW in your phone's Settings.");
      return;
    }
    if (res.reason === "unsupported") { setPushNeeded(false); return; }
    toast.error("Couldn't turn on notifications. Try again in a moment.");
  };

  const snooze = () => {
    try { localStorage.setItem(SNOOZE_KEY(crewId), String(Date.now() + SNOOZE_MS)); } catch (_) { /* ignore */ }
    setSnoozed(true);
  };

  return (
    <div data-testid="home-setup-nudge" className="rounded-2xl p-4 bg-white shadow-sm border border-amber-200 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-widest text-amber-600">Finish setting up</p>
          <p className="text-sm font-bold text-slate-800">A couple quick things so you don't miss out</p>
        </div>
        <button
          type="button"
          onClick={snooze}
          data-testid="home-setup-nudge-later"
          aria-label="Remind me later"
          className="text-slate-400 hover:text-slate-600 p-1 -m-1 flex-none"
        >
          <X weight="bold" className="w-4 h-4" />
        </button>
      </div>

      {needPush && (
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          data-testid="home-setup-enable-push"
          className="w-full flex items-center gap-3 rounded-xl px-3 py-3 bg-amber-50 active:scale-[0.99] transition-transform text-left disabled:opacity-60"
        >
          <span className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center flex-none">
            <Bell weight="fill" className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-slate-800">Turn on notifications</span>
            <span className="block text-xs text-slate-500">Get High Fives, shout-outs &amp; shift alerts</span>
          </span>
          <span className="text-xs font-black text-amber-600 flex-none">{busy ? "…" : "Turn on"}</span>
        </button>
      )}

      {needPhoto && (
        <button
          type="button"
          onClick={onAddPhoto}
          data-testid="home-setup-add-photo"
          className="w-full flex items-center gap-3 rounded-xl px-3 py-3 bg-amber-50 active:scale-[0.99] transition-transform text-left"
        >
          <span className="w-9 h-9 rounded-full bg-rose-500 text-white flex items-center justify-center flex-none">
            <Camera weight="fill" className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-slate-800">Add your photo</span>
            <span className="block text-xs text-slate-500">So your High Fives &amp; stories show your face</span>
          </span>
          <span className="text-xs font-black text-rose-600 flex-none">Add</span>
        </button>
      )}
    </div>
  );
}
